import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { RollingAction } from '@diritto/common/rolling/constants';
import { RollingCreateDto, RollingEditDto } from '@diritto/common/rolling/dtos/rolling.dto';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { RefetchOptions, RefetchQueryFilters, useQueryClient } from '@tanstack/react-query';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useRouter } from 'next/navigation';
import { relayQueryKey } from 'queries/relay/queryKey';
import { rollingsQueryKey } from 'queries/rolling/queryKey';
import { useDeleteRollingMutation } from 'queries/rolling/useDeleteRollingMutation';
import { useCallback, useMemo, useState } from 'react';
import { useShare } from 'ui-hooks/useShare';
import { showToast } from 'utils/modal';

type BaseRollingDispatchParams = {
  action: RollingAction;
  rolling: RollingRelayDto;
};

export type ShareRollingDispatchParams = {
  action: RollingAction.Share;
  rolling: RollingRelayDto;
};

export type CreateRollingDispatchParams = {
  action: RollingAction.Create;
  contentId?: number;
  data: RollingCreateDto;
  onMutate?: OnMutateFn;
};

export type UpdateRollingDispatchParams = {
  action: RollingAction.Update;
  rolling: RollingRelayDto;
  data: RollingEditDto;
  onMutate?: OnMutateFn;
};

export type DeleteRollingDispatchParams = {
  action: RollingAction.Delete;
  rolling: RollingRelayDto;
  onMutate?: OnMutateFn;
};

export type GoToAddRollingDispatchParams = {
  action: RollingAction.GoToAdd;
};

export type GoToEditRollingDispatchParams = {
  action: RollingAction.GoToEdit;
  rolling: RollingRelayDto;
};

export type RollingDispatchParams =
  | BaseRollingDispatchParams
  | CreateRollingDispatchParams
  | UpdateRollingDispatchParams
  | DeleteRollingDispatchParams
  | ShareRollingDispatchParams
  | GoToAddRollingDispatchParams
  | GoToEditRollingDispatchParams;

export type RollingDispatchState = {
  isPending: boolean;
  rolling?: RollingRelayDto;
  isInputFocus: boolean;
  dispatchAction: (params: RollingDispatchParams) => void;
};

export const useRollingDispatch = (refetch?: (options?: RefetchOptions & RefetchQueryFilters) => Promise<any>) => {
  const queryClient = useQueryClient();
  const { tracker } = useTrackingState();
  const handleShare = useShare();
  const router = useRouter();

  const [dispatchState, setDispatchState] = useState<{
    editRolling?: RollingRelayDto;
    isInputFocus: boolean;
  }>({
    editRolling: undefined,
    isInputFocus: false,
  });

  const onSettled = useCallback(() => {
    setDispatchState(prev => ({ ...prev, editReply: undefined, isInputFocus: false }));
    refetch?.();
  }, [refetch]);

  const { mutate: deleteMutate, isPending: isDeleteMutatePending } = useDeleteRollingMutation({
    onSettled,
    onSuccess: (_, { rollingId }) => {
      showToast('롤링이 삭제되었어요.');
      queryClient.invalidateQueries({ queryKey: relayQueryKey.relayRolling });
      queryClient.invalidateQueries({ queryKey: rollingsQueryKey.key });
      tracker.track(TrackingEventType.RollingAction, { action: RollingAction.Delete, rollingId });
    },
    onError: () => showToast('삭제에 실패했습니다. 다시 시도해주세요.'),
  });

  const handleDispatchAction = useCallback(
    async (params: RollingDispatchParams) => {
      const { action } = params;

      switch (params.action) {
        case RollingAction.Share: {
          tracker.track(TrackingEventType.RollingAction, { action: RollingAction.Share, rollingId: params.rolling.id });

          const pageUrl = `${window.location.origin}/rollings/${params.rolling.id}`;

          await handleShare({
            title: params.rolling.title,
            text: `${params.rolling.title}\n#디리토_롤링\n${pageUrl}`,
            url: pageUrl,
          });
          break;
        }
        case RollingAction.Delete:
          deleteMutate({ rollingId: params.rolling.id });
          break;
        case RollingAction.GoToEdit:
          router.push(`/rollings/${params.rolling.id}/edit`);
          break;
        case RollingAction.GoToAdd:
          router.push(`/rollings/add`);
          break;
        default:
          console.debug('Not implemented yet', action);
      }
    },
    [deleteMutate, handleShare, router, tracker],
  );

  return useMemo<RollingDispatchState>(
    () => ({
      isPending: isDeleteMutatePending,
      rolling: dispatchState.editRolling,
      isInputFocus: dispatchState.isInputFocus,
      dispatchAction: handleDispatchAction,
    }),
    [handleDispatchAction, isDeleteMutatePending, dispatchState.editRolling, dispatchState.isInputFocus],
  );
};
